import { useState } from "react";
import type { CreditProduct } from "api";
import cn from "classnames";
import { cubeUrl } from "constants/products.constants";
import { FAQ, formatCredits, PRICE_LIST_PREVIEW, PRODUCT_BLURBS } from "./credits.model";

type PriceListCardProps = {
  products: CreditProduct[];
};

type Tab = "prices" | "faq";

const HEADINGS: Record<Tab, { title: string; sub: string }> = {
  prices: { title: "Where you can use your QCredits", sub: "Each action spends a fixed number of QCredits." },
  faq: { title: "QCredits FAQ", sub: "Answers about payments and your balance." },
};

const Chevron = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Arrow = () => (
  <svg className="pru__go" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * For a company that has not bought anything yet: what every action costs,
 * from the back office's price list, and the payment FAQ.
 */
export const PriceListCard = ({ products }: PriceListCardProps) => {
  const [tab, setTab] = useState<Tab>("prices");
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const shown = all ? products : products.slice(0, PRICE_LIST_PREVIEW);

  return (
    <div className="pcx">
      <div className="pcx__top">
        <div>
          <div className="pcx__h">{HEADINGS[tab].title}</div>
          <p className="pcx__sub">{HEADINGS[tab].sub}</p>
        </div>
        <div className="acv__seg" role="tablist" aria-label="QCredits info">
          {(["prices", "faq"] as const).map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={cn("acv__b", { "is-on": tab === key })}
              onClick={() => setTab(key)}
            >
              {key === "prices" ? "Prices" : "FAQ"}
            </button>
          ))}
        </div>
      </div>

      {tab === "faq" ? (
        <div className="acq">
          {FAQ.map((item, index) => (
            <article key={item.q} className={cn("acq__row", { "is-open": open === index })}>
              <button
                type="button"
                className="acq__rowhead"
                aria-expanded={open === index}
                onClick={() => setOpen(open === index ? null : index)}
              >
                <span className="acq__idx">{String(index + 1).padStart(2, "0")}</span>
                <h3 className="acq__q">{item.q}</h3>
                <span className="acq__sign" />
              </button>
              <div className="acq__a">
                <p>{item.a}</p>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <>
          <div className="pru" role="table" aria-label="QCredit cost per product">
            <div className="pru__hd" role="row">
              <span role="columnheader">Tools</span>
              <span role="columnheader">QCredits</span>
              <span role="columnheader">Description</span>
            </div>
            <div className="pru__list">
              {shown.map((product) => {
                const body = (
                  <>
                    <span className="pru__t">
                      {product.cube !== null && <img src={cubeUrl(product.cube)} alt="" width={36} height={36} />}
                      {product.name}
                    </span>
                    <span className="pru__c">
                      <b>{formatCredits(product.credits)}</b> / 1 {product.unit}
                    </span>
                    <span className="pru__d">{PRODUCT_BLURBS[product.product] ?? ""}</span>
                  </>
                );

                return product.url === null ? (
                  <div className="pru__r" key={product.product}>
                    {body}
                  </div>
                ) : (
                  <a className="pru__r" key={product.product} href={product.url}>
                    {body}
                    <Arrow />
                  </a>
                );
              })}
            </div>
          </div>
          {products.length > PRICE_LIST_PREVIEW && (
            <button type="button" className="acr__all" aria-expanded={all} onClick={() => setAll(!all)}>
              <span>{all ? "Show less" : "Show all"}</span>
              <Chevron />
            </button>
          )}
        </>
      )}
    </div>
  );
};
